import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { rawFetch } from "../api/client";
import { useT } from "../i18n/I18nProvider";

/** Images are fetched with the admin's token (the API serves them to platform admins only); object URLs are shared
 * across pages of a session. */
const imageCache = new Map<string, Promise<string>>();
function imageUrl(name: string): Promise<string> {
  let url = imageCache.get(name);
  if (!url) {
    url = rawFetch(`/api/v1/admin/library/images/${encodeURIComponent(name)}`)
      .then((r) => r.blob())
      .then((b) => URL.createObjectURL(b));
    url.catch(() => imageCache.delete(name));
    imageCache.set(name, url);
  }
  return url;
}

/**
 * A Library page body: trusted HTML shipped with the API (app/design/library/content). Images
 * (`<img data-library-image>`) load through the authenticated API and open full size on click; links to other
 * admin pages stay in the app, outside links open in a new tab; `<div data-library-mount="name">` gets the
 * interactive view `mounts[name]`.
 */
export function LibraryContent({ html, mounts = {}, className }: { html: string; mounts?: Record<string, ReactNode>; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [targets, setTargets] = useState<[string, Element][]>([]);
  const [zoom, setZoom] = useState<string | null>(null);
  const t = useT();

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.innerHTML = html;
    setTargets([...root.querySelectorAll("[data-library-mount]")].map((el) => [el.getAttribute("data-library-mount") || "", el]));
    for (const a of root.querySelectorAll<HTMLAnchorElement>("a[href^='http']")) {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    }
    let live = true;
    for (const img of root.querySelectorAll<HTMLImageElement>("img[data-library-image]")) {
      const name = img.dataset.libraryImage || "";
      img.loading = "lazy";
      imageUrl(name)
        .then((url) => {
          if (live) img.src = url;
        })
        .catch(() => {
          if (live) img.replaceWith(Object.assign(document.createElement("div"), { className: "lib-img-missing", textContent: t("library.image.missing", { name }) }));
        });
    }
    // Scroll to the URL's anchor once the HTML exists (the router does not know about it).
    if (window.location.hash) document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView();
    return () => {
      live = false;
    };
  }, [html, t]);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const img = target.closest("img[data-library-image]") as HTMLImageElement | null;
      if (img?.src) {
        event.preventDefault();
        setZoom(img.src);
        return;
      }
      const link = target.closest("a") as HTMLAnchorElement | null;
      const href = link?.getAttribute("href") || "";
      if (link && href.startsWith("/admin/") && !event.metaKey && !event.ctrlKey && event.button === 0) {
        event.preventDefault();
        navigate(href);
      } else if (link && href.startsWith("#")) {
        event.preventDefault();
        document.getElementById(decodeURIComponent(href.slice(1)))?.scrollIntoView({ behavior: "smooth" });
        history.replaceState(null, "", href);
      }
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [navigate]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  return (
    <>
      <div ref={ref} className={className} />
      {targets.map(([name, el]) => (mounts[name] ? createPortal(mounts[name], el, name) : null))}
      {zoom && (
        <div className="lib-zoom" role="dialog" aria-label={t("library.image.zoom")} onClick={() => setZoom(null)}>
          <img src={zoom} alt="" />
        </div>
      )}
    </>
  );
}
